import React from 'react'
import { useStateWithDebounce, useCompanies } from '@modmd/data'
import { Dropdown, DropdownOnSearchChangeData } from 'components/AutoCompleteDropdown'

const COMPANIES_IN_DROPDOWN = 5

interface Props {
  value?: string
  onChange: (
    id: string,
    settings?: {
      pcrOnly: boolean
      insuranceOnly: boolean
      automaticallyApproved: boolean
    }
  ) => void
  hasBottomContent?: boolean
  isDisabled?: boolean
}

export const CompaniesDropdown: React.VFC<Props> = ({
  value,
  onChange,
  hasBottomContent,
  isDisabled = false,
}) => {
  const [hasDefaultValueFromUrl, setHasDefaultValueFromUrl] = React.useState(!!value)
  const [, setSearchQuery, debouncedSearchQuery] = useStateWithDebounce('')

  const { data, loading } = useCompanies({
    filter: hasDefaultValueFromUrl
      ? { id: value }
      : {
          nameOrGroupNameLike: debouncedSearchQuery,
        },
    pageLength: hasDefaultValueFromUrl ? 1 : COMPANIES_IN_DROPDOWN,
  })

  const handleSearchChange = React.useCallback(
    (_: React.SyntheticEvent<HTMLElement>, data: DropdownOnSearchChangeData) => {
      setSearchQuery(data.searchQuery)
      setHasDefaultValueFromUrl(false)
    },
    [setSearchQuery]
  )

  const handleChange = (id: string) => {
    const company = data?.companies?.companies.find((company) => company.id === id)
    onChange(id, {
      pcrOnly: company?.pcrOnly || false,
      insuranceOnly: company?.insuranceOnly || false,
      automaticallyApproved: company?.automaticallyApproved || false,
    })
  }

  return (
    <Dropdown
      label="Company"
      value={value}
      options={
        hasDefaultValueFromUrl
          ? [{ value, text: data?.companies?.companies[0].name }]
          : data?.companies?.companies.map((company) => ({
              value: company.id,
              text: company.name,
            }))
      }
      onSelectChange={(_: React.SyntheticEvent<HTMLElement>, data: { value: string }) => {
        handleChange(data.value)
        setHasDefaultValueFromUrl(false)
      }}
      onSearchChange={handleSearchChange}
      isLoading={loading}
      selectOnNavigation={false}
      hasBottomContent={hasBottomContent}
      disabled={isDisabled}
    />
  )
}
